import { StrictMode, Suspense, lazy } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { MotionConfig } from "framer-motion";
import { AuthProvider } from "@/hooks/useAuth";
import { AuthGate } from "@/components/AuthGate";
import { queryClient } from "@/lib/queryClient";
import "@/styles/base.css";
import "@/styles/primitives.css";

/*
 * The dashboard is lazy, and that is load-bearing rather than tidy.
 *
 * "/" is now a public marketing page and the destination of paid ads, so a
 * first-time visitor's first paint must not carry the app. Imported eagerly,
 * App pulls AppShell — sidebar, mobile nav, command palette, session hooks and
 * the whole route registry — into the entry chunk for a reader who may never
 * sign in. Behind React.lazy that graph becomes a separate chunk, which
 * AuthGate only reaches for once a session exists.
 *
 * MotionConfig reducedMotion="user" makes every Framer animation in the tree
 * honour the OS setting without each component checking. GSAP is handled in
 * motion/gsap.ts and CSS in tokens.css — all three read the same signal.
 */
const App = lazy(() => import("./App"));

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <MotionConfig reducedMotion="user">
        <BrowserRouter>
          <AuthProvider>
            <AuthGate>
              {/* Blank, not a spinner: AuthGate has already resolved the session
                  by the time this renders, and the shell paints its own
                  skeleton per route. */}
              <Suspense fallback={null}>
                <App />
              </Suspense>
            </AuthGate>
          </AuthProvider>
        </BrowserRouter>
      </MotionConfig>
    </QueryClientProvider>
  </StrictMode>,
);
